import {Component, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {UntypedFormBuilder} from '@angular/forms';
import {AlertService} from '../../../sdk/service/alert.service';
import {ROUTES} from '../../../app.routes';
import {Account, AccountUpdate, WebAccountService} from 'web-angular-client';
import {AuthenticationService} from '../../../sdk/service/authentication.service';
import {ConfirmationService} from '../../../sdk/service/confirmation.service';
import {switchMap} from 'rxjs/operators';
import {Observable, Subject} from 'rxjs';
import {NgxUiLoaderService} from 'ngx-ui-loader';

@Component({
    selector: 'app-payment-request-edit',
    templateUrl: './payment-request-edit.component.html',
    styleUrls: ['./payment-request-edit.component.scss'],
    standalone: false
})
export class PaymentRequestEditComponent implements OnInit
{
    private accountId: number;
    private accountSubject: Subject<Account> = new Subject();

    constructor(
        protected router: Router,
        private route: ActivatedRoute,
        protected formBuilder: UntypedFormBuilder,
        protected alertService: AlertService,
        private ngxLoader: NgxUiLoaderService,
        protected authenticationService: AuthenticationService,
        protected confirmationService: ConfirmationService,
        private webAccountService: WebAccountService)
    {
    }

    ngOnInit()
    {
        this.loadAccountFromParams().subscribe({
            next: account =>
            {
                this.accountSubject.next(account);
            },
            error: err =>
            {
                this.router.navigate([ROUTES.SECURE.HOME.VIEW.path]).then();
                this.alertService.error(err, true);
            }
        });
    }

    cancel(): void
    {
        this.confirmationService.cancelConfirmation('request to pay transaction').then((confirmed) =>
        {
            if (confirmed)
            {
                this.router.navigate([ROUTES.SECURE.HOME.VIEW.path]).then();
            }
        });
    }

    getAccountSubject()
    {
        return this.accountSubject;
    }

    /**
     *  Opens a confirmation dialogue prompting the user to accept their action. On successfully
     *  confirming, the updated account will be posted.
     */
    onAccountSubmission(account: Account): void
    {
        this.confirmationService.saveConfirmation().then((confirmed) =>
        {
            if (confirmed)
            {
                const updatedAccount: AccountUpdate = this.mapAccountUpdateFromAccount(account);
                this.ngxLoader.start();
                this.webAccountService.updateAccount(this.accountId, updatedAccount).subscribe({
                    next: (updatedPayment) =>
                    {
                        this.ngxLoader.stop();
                        this.router.navigate([ROUTES.SECURE.ACCOUNT.SEND.pathSansParameters, updatedPayment.id]).then();
                    }, error: (error) =>
                    {
                        this.ngxLoader.stop();
                        this.alertService.error(error);
                    }
                });
            }
        });
    }

    /**
     * Retrieve the account ID from the URL path and use it to retrieve the matching account.
     */
    protected loadAccountFromParams(): Observable<Account>
    {
        return this.route.params.pipe(switchMap(params =>
        {
            this.accountId = params['id'];
            return this.webAccountService.getAccountById(this.accountId);
        }));
    }

    private mapAccountUpdateFromAccount(account: Account): AccountUpdate
    {
        const accountUpdate = {} as AccountUpdate;

        accountUpdate.lineItems = account.lineItems;
        accountUpdate.amount = account.amount;
        accountUpdate.minimumAmount = account.minimumAmount;
        accountUpdate.maximumAmount = account.maximumAmount;
        accountUpdate.multiPremium = account.multiPremium;
        accountUpdate.customer = account.customer;
        accountUpdate.customerMobileNumber = account.customerMobileNumber;
        accountUpdate.customerEmail = account.customerEmail;
        accountUpdate.customerVatNumber = account.customerVatNumber;
        accountUpdate.referenceNumber = account.referenceNumber;
        accountUpdate.description = account.description;
        accountUpdate.notificationMobileNumber = account.notificationMobileNumber;
        accountUpdate.dateTimeExpire = account.dateTimeExpire;

        return accountUpdate;
    }
}